import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type Resolver } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useLocaleNavigate } from '@lib/i18n/exports';
import { z } from 'zod';
import {
  useEmailValidationStatus,
  usePasskeyLogin,
  useSendOtp,
  parsePasskeyError,
  type VerifyOtpResult
} from '@features/auth/hooks';
import { getAndClearReturnUrl, isPasskeySupported } from '@lib/api/auth';
import { useAttributeStoredReferral } from '@lib/api/ashrafie';
import { useToastActions } from '@shared/ui/toast';

import { useAuth } from '@/hooks/useAuth';
import {
  clearLoginFlow,
  loadLoginFlow,
  saveLoginFlow
} from './multiMethod/flowStorage';
import { useTranslation } from 'react-i18next';
import { SocialLogin } from '@lib/SocialLogin';
import { EmailStep } from './multiMethod/EmailStep';
import { NameStep } from './multiMethod/NameStep';
import { OtpStep } from './multiMethod/OtpStep';
import { PasswordStep } from './multiMethod/PasswordStep';
import type { LoginStep, MultiMethodLoginFormData } from './multiMethod/types';
import { stepVariants } from './multiMethod/types';

interface MultiMethodLoginFormProps {
  variant?: 'standard' | 'compact';
  mode?: 'page' | 'overlay';
  onAuthenticated?: () => void;
  onStepChange?: (step: LoginStep, isNewUser: boolean) => void;
}

/**
 * MultiMethodLoginForm
 * Email-first auth flow with progressive disclosure:
 * - Email OTP (primary)
 * - Passkey (secondary)
 * - Password (tertiary)
 */
export const MultiMethodLoginForm: React.FC<MultiMethodLoginFormProps> = ({
  variant = 'standard',
  mode = 'page',
  onAuthenticated,
  onStepChange
}) => {
  const { t } = useTranslation('auth');
  const isCompact = variant === 'compact';
  const isOverlay = mode === 'overlay';
  const ui = useMemo(
    () =>
      ({
        headerTitle: isCompact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl',
        headerSubtitle: isCompact ? 'text-sm' : 'text-sm sm:text-base',
        headerSpacing: isCompact ? 'mb-6' : 'mb-8',
        inputHeight: isCompact ? 'h-12' : 'h-14',
        primaryBtnHeight: isCompact ? 'h-12' : 'h-14',
        sectionGap: isCompact ? 'space-y-4' : 'space-y-5',
        altBtnHeight: isCompact ? 'h-11' : 'h-12'
      }) as const,
    [isCompact]
  );

  const navigate = useLocaleNavigate();
  const location = useLocation();
  const { login, authState, completeAuthentication } = useAuth();
  const { success: showSuccess } = useToastActions();
  const attributeStoredReferral = useAttributeStoredReferral();

  const passkeySupported = isPasskeySupported();

  const [step, setStep] = useState<LoginStep>(
    () => loadLoginFlow()?.step ?? 'email'
  );
  const [email, setEmail] = useState(() => loadLoginFlow()?.email ?? '');
  const [isNewUser, setIsNewUser] = useState(
    () => loadLoginFlow()?.isNewUser ?? false
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Notify parent (AuthSheet) when step changes so it can adapt its header
  useEffect(() => {
    onStepChange?.(step, isNewUser);
  }, [step, isNewUser, onStepChange]);

  const {
    status: emailValidationStatus,
    invalidReason: emailInvalidReason,
    connect: connectEmailValidation,
    reset: resetEmailValidation
  } = useEmailValidationStatus({
    onInvalid: reason => {
      setError(reason);
    }
  });

  const formSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    rememberMe: z.boolean().optional()
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setValue
  } = useForm<MultiMethodLoginFormData, unknown, MultiMethodLoginFormData>({
    // formSchema validates a subset of the registered fields (email, password,
    // firstName, lastName, rememberMe); termsOfService/privacyPolicy/marketingEmail
    // are registered on the form but intentionally not part of the schema. Cast the
    // resolver to the form's field type so the extra fields stay typed for the child
    // steps -- runtime validation is unchanged.
    resolver: zodResolver(formSchema) as unknown as Resolver<
      MultiMethodLoginFormData,
      unknown,
      MultiMethodLoginFormData
    >,
    mode: 'onChange'
  });

  // Restore email field after reload if user progressed beyond first step.
  useEffect(() => {
    const saved = loadLoginFlow();
    if (saved?.email && saved.step !== 'email') {
      setValue('email', saved.email, { shouldValidate: true });
    }
  }, [setValue]);

  const emailValue = watch('email');
  const passwordValue = watch('password');
  const firstNameValue = watch('firstName');
  const lastNameValue = watch('lastName');

  const isEmailValid = Boolean(emailValue && !errors.email);
  const isPasswordValid = Boolean(passwordValue && passwordValue.length > 0);
  const isNameValid =
    Boolean(firstNameValue && firstNameValue.trim().length >= 2) &&
    Boolean(lastNameValue && lastNameValue.trim().length >= 2);

  const termsValue = watch('termsOfService');
  const privacyValue = watch('privacyPolicy');
  const isConsentValid = termsValue === true && privacyValue === true;

  const handleClearLoginFlow = useCallback((): void => {
    clearLoginFlow();
  }, []);

  const handleResetToEmail = useCallback((): void => {
    setStep('email');
    setError(null);
    setInfoMessage(null);
    setIsNewUser(false);
    resetEmailValidation();
    handleClearLoginFlow();
  }, [handleClearLoginFlow, resetEmailValidation]);

  const handleLoginSuccess = useCallback((): void => {
    handleClearLoginFlow();

    const storedReturnUrl = getAndClearReturnUrl();
    const stateReturnUrl = (location.state as { from?: string } | null)?.from;
    const returnToChat =
      sessionStorage.getItem('amalq_return_to_chat') === 'true';
    const chatReturnUrl =
      sessionStorage.getItem('amalq_chat_return_url') || '/';
    const returnUrl =
      storedReturnUrl ||
      stateReturnUrl ||
      (returnToChat ? chatReturnUrl : '/dashboard');

    if (returnToChat) {
      sessionStorage.removeItem('amalq_return_to_chat');
      sessionStorage.removeItem('amalq_chat_return_url');
    }

    if (isOverlay) {
      if (returnToChat) {
        sessionStorage.setItem('amalq_reopen_chat', 'true');
      }
      onAuthenticated?.();
      return;
    }

    showSuccess(
      'Welcome to AmalQ! 🌙',
      'You have been successfully logged in.'
    );

    setTimeout(() => {
      navigate(returnUrl, { replace: true });
      if (returnToChat) {
        sessionStorage.setItem('amalq_reopen_chat', 'true');
      }
    }, 1500);
  }, [
    handleClearLoginFlow,
    isOverlay,
    location.state,
    navigate,
    onAuthenticated,
    showSuccess
  ]);

  const { mutate: sendOtp, isPending: isSendingOtp } = useSendOtp({
    onSuccess: result => {
      setEmail(result.email);
      setIsNewUser(result.isNewUser);

      const nextStep: LoginStep =
        result.isNewUser && !result.otpSent ? 'name' : 'otp';
      setStep(nextStep);
      setError(null);
      setInfoMessage(null);

      saveLoginFlow({
        step: nextStep,
        email: result.email,
        isNewUser: result.isNewUser
      });

      if (result.validationJobId) {
        connectEmailValidation(result.validationJobId);
      }
    },
    onError: err => {
      setInfoMessage(null);
      setError(
        err.message === 'CAPTCHA_REQUIRED'
          ? 'Security verification required. Please try again.'
          : err.message || 'Failed to send verification code'
      );
    }
  });

  const { mutate: loginWithPasskey, isPending: isPasskeyLoading } =
    usePasskeyLogin({
      onSuccess: async result => {
        await completeAuthentication({
          user: result.user,
          tokens: {
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
            expiresIn: result.tokens.expiresIn,
            tokenType: 'Bearer'
          }
        });
        handleLoginSuccess();
      },
      onError: err => {
        const passkeyError = parsePasskeyError(err);
        if (passkeyError.code === 'USER_CANCELLED') return;
        if (passkeyError.code === 'NO_PASSKEYS') {
          setInfoMessage(passkeyError.message);
          setError(null);
          return;
        }
        setError(passkeyError.message);
        setInfoMessage(null);
      }
    });

  const handleContinue = useCallback(
    (data: MultiMethodLoginFormData) => {
      if (!data.email || !isEmailValid) {
        setError('Please enter a valid email address');
        return;
      }

      setEmail(data.email);
      setError(null);
      setInfoMessage(null);
      sendOtp({ email: data.email });
    },
    [isEmailValid, sendOtp]
  );

  const handlePasskeyLogin = useCallback((): void => {
    const currentEmail = getValues('email');
    if (!currentEmail || errors.email) {
      setError('Please enter your email first');
      return;
    }

    setEmail(currentEmail);
    setError(null);
    setInfoMessage(null);
    loginWithPasskey(currentEmail);
  }, [errors.email, getValues, loginWithPasskey]);

  const handleChoosePassword = useCallback((): void => {
    const currentEmail = getValues('email');
    if (currentEmail && !errors.email) {
      setEmail(currentEmail);
      setStep('password');
      setError(null);
      setInfoMessage(null);
      saveLoginFlow({
        step: 'password',
        email: currentEmail,
        isNewUser: false
      });
      return;
    }
    setError('Please enter your email first');
  }, [errors.email, getValues]);

  const handlePasswordLogin = useCallback(
    async (data: MultiMethodLoginFormData) => {
      if (!data.password) {
        setError('Please enter your password');
        return;
      }

      setError(null);
      setInfoMessage(null);

      try {
        const ok = await login(data.email, data.password);
        if (ok) {
          handleLoginSuccess();
          return;
        }

        const pendingLogin = sessionStorage.getItem('amalq_pending_login');
        if (pendingLogin) {
          navigate('/verify-2fa');
          return;
        }

        setError('Invalid email or password');
      } catch {
        setError('Login failed. Please try again.');
      }
    },
    [handleLoginSuccess, login, navigate]
  );

  const handleOtpSuccess = useCallback(
    async (
      user: VerifyOtpResult['user'],
      tokens: VerifyOtpResult['tokens'],
      newUser: boolean
    ) => {
      handleClearLoginFlow();

      await completeAuthentication({
        user,
        tokens: {
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresIn: tokens.expiresIn,
          tokenType: 'Bearer'
        }
      });

      if (newUser) {
        // Genuinely new account (OTP register path) — attribute a captured
        // Ashrafie referral. Fire-and-forget, non-blocking, error-swallowing.
        attributeStoredReferral();

        showSuccess(
          'Welcome to AmalQ! 🌙',
          "Your account has been created. Let's get started!"
        );
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
        return;
      }

      handleLoginSuccess();
    },
    [
      attributeStoredReferral,
      completeAuthentication,
      handleClearLoginFlow,
      handleLoginSuccess,
      navigate,
      showSuccess
    ]
  );

  const handleNameSubmit = useCallback(
    (data: MultiMethodLoginFormData) => {
      if (!data.firstName?.trim() || !data.lastName?.trim()) {
        setError('Please enter your first and last name');
        return;
      }

      if (data.firstName.trim().length < 2 || data.lastName.trim().length < 2) {
        setError('Names must be at least 2 characters');
        return;
      }

      const currentEmail = email || getValues('email');
      if (!currentEmail) {
        setError('Email is missing. Please start over.');
        handleResetToEmail();
        return;
      }

      setError(null);
      sendOtp({
        email: currentEmail,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        consents: {
          termsOfService: true,
          privacyPolicy: true,
          marketingEmail: data.marketingEmail ?? false,
        }
      });
    },
    [email, getValues, handleResetToEmail, sendOtp]
  );

  const isLoading = isSendingOtp || isPasskeyLoading || authState.isLoading;

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {step === 'email' && (
          <motion.div
            key="email"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {/* Social Login -- fastest path first */}
            <SocialLogin
              onAuthenticated={isOverlay ? onAuthenticated : undefined}
            />

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-xs uppercase tracking-wider text-gray-400">
                  {t('multiMethod.or', 'or')}
                </span>
              </div>
            </div>

            {/* Email + OTP form */}
            <EmailStep
              ui={ui}
              error={error}
              infoMessage={infoMessage}
              register={register}
              errors={errors}
              handleSubmit={handleSubmit}
              onContinue={handleContinue}
              isEmailValid={isEmailValid}
              isLoading={isLoading}
              isSendingOtp={isSendingOtp}
              passkeySupported={passkeySupported}
              isPasskeyLoading={isPasskeyLoading}
              onPasskeyLogin={handlePasskeyLogin}
              onChoosePassword={handleChoosePassword}
              autoFocus={isOverlay}
              showHeader={false}
            />
          </motion.div>
        )}

        {step === 'name' && (
          <motion.div
            key="name"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <NameStep
              ui={ui}
              email={email}
              error={error}
              isNameValid={isNameValid}
              isConsentValid={isConsentValid}
              isSendingOtp={isSendingOtp}
              emailValidationStatus={emailValidationStatus}
              emailInvalidReason={emailInvalidReason}
              onChangeEmail={handleResetToEmail}
              register={register}
              errors={errors}
              handleSubmit={handleSubmit}
              onSubmitName={handleNameSubmit}
            />
          </motion.div>
        )}

        {step === 'password' && (
          <motion.div
            key="password"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <PasswordStep
              ui={ui}
              email={email}
              error={error}
              showPassword={showPassword}
              onToggleShowPassword={() => setShowPassword(v => !v)}
              isPasswordValid={isPasswordValid}
              isSubmitting={authState.isLoading}
              register={register}
              handleSubmit={handleSubmit}
              onSubmitPassword={handlePasswordLogin}
              onBack={handleResetToEmail}
            />
          </motion.div>
        )}

        {step === 'otp' && (
          <motion.div
            key="otp"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <OtpStep
              email={email}
              isNewUser={isNewUser}
              emailValidationStatus={emailValidationStatus}
              emailInvalidReason={emailInvalidReason}
              onChangeEmail={handleResetToEmail}
              onBack={handleResetToEmail}
              onSuccess={handleOtpSuccess}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
